import { useState } from 'react';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { getItemWithExpiration } from '../lib/utils';
import {
    deleteMembershipPayment,
    patchMembershipRefund,
    PatchMembershipRefundAndUpgradedParams,
    patchMembershipUpgrade,
    postSalesManager,
    postSplitPayment,
    PostSplitPaymentParams,
} from '../api/membershipPayments';
import { SalesManager } from './useMembershipPaymentsQueryService';
import { patchMembershipPayment, PatchMembershipPaymentApiType } from '../api/membershipPayments';
import { invalidateSuperAdminDashboardQueries } from '../lib/superAdminCache';
// barrel(features/sharedMembership) 대신 keys 직접 참조 — barrel은 ESM-only ky까지 즉시 평가한다.
import { sharedMembershipKeys } from '../features/sharedMembership/keys';
import { teamKeys } from '../features/team/keys';

/** 판매/결제 변경 뒤 다시 계산되는 화면을 한곳에서 빠짐없이 갱신한다. */
export const invalidateMembershipPaymentRelatedQueries = async (queryClient: QueryClient) => {
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getUnpaidPaymentsQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getSalesListQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] }),
        queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleFeeQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleSessionQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getSalesCommissionQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['groupTicket'] }),
        queryClient.invalidateQueries({ queryKey: ['publicMember'] }),
        queryClient.invalidateQueries({ queryKey: ['membershipChangeLog'] }),
        queryClient.invalidateQueries({ queryKey: ['salesRecordMembershipDetail'] }),
        queryClient.invalidateQueries({ queryKey: teamKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['getFeedDashboardQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getFeedDashboardCoachDetailQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterCoachRanking'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterCoachMetricEvidence'] }),
        queryClient.invalidateQueries({ queryKey: ['getReRegistrationSuccessMembers'] }),
        queryClient.invalidateQueries({ queryKey: sharedMembershipKeys.all }),
        invalidateSuperAdminDashboardQueries(queryClient),
    ]);
};

type Props = {
    onPostSalesManagerSuccess?: (data: SalesManager[]) => void;
    /** 서버 응답을 그대로 넘긴다 — 화면이 `accountingOperation.replayed`로 재생 여부를 구분한다. */
    onPatchMembershipRefundSuccess?: (result: { status?: number; data?: any } | null | undefined) => void;
    onPatchMembershipUpgradeSuccess?: (result: { status?: number; data?: any } | null | undefined) => void;
    onPostSplitPaymentSuccess?: () => void;
    onPatchMembershipPaymentSuccess?: () => void;
    onDeleteMembershipPaymentSuccess?: () => void;
    /** 결제 이력 수정이 200 이 아닌 채로 끝났을 때. 공유 중 차단(409)이 여기로 온다 */
    onPatchMembershipPaymentFail?: (result: { status?: number; data?: any } | null | undefined) => void;
    /** 결제 이력 삭제가 204/200 이 아닌 채로 끝났을 때 */
    onDeleteMembershipPaymentFail?: (result: { status?: number; data?: any } | null | undefined) => void;
};

const useMembershipPaymentsMutationService = ({
    onPostSalesManagerSuccess,
    onPatchMembershipRefundSuccess,
    onPatchMembershipUpgradeSuccess,
    onPostSplitPaymentSuccess,
    onPatchMembershipPaymentSuccess,
    onDeleteMembershipPaymentSuccess,
    onPatchMembershipPaymentFail,
    onDeleteMembershipPaymentFail,
}: Props) => {
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();

    // 결제계 변경은 회원권 상세뿐 아니라 매출목록/미수금/정산달력/급여계산까지 연쇄 영향이 있다.
    const invalidatePaymentRelatedQueries = () => invalidateMembershipPaymentRelatedQueries(queryClient);

    // 회원권 이전하기
    const postSalesManagerMutation = useMutation(
        ['postSalesManagerMutation'],
        async (params: { membershipId: string; managerName: string }) =>
            postSalesManager(params.membershipId, params.managerName, authorization),

        {
            onError: (error) => {
                console.log(error);
            },
            onSuccess: async ({ status, data }) => {
                if (status === 201) {
                    const result = data.salesManagers as SalesManager[];
                    onPostSalesManagerSuccess && onPostSalesManagerSuccess(result);
                }
            },
        }
    );
    // 회원권 환불
    const patchMembershipRefundMutation = useMutation(
        ['patchMembershipRefundMutation'],
        async (params: Omit<PatchMembershipRefundAndUpgradedParams, 'accessToken'>) =>
            patchMembershipRefund({ ...params, accessToken: authorization }),
        {
            onError: (error) => {
                console.log(error);
            },
            onSuccess: async ({ status, data }) => {
                if (status === 200 || status === 201) {
                    onPatchMembershipRefundSuccess && onPatchMembershipRefundSuccess({ status, data });
                    void invalidatePaymentRelatedQueries();
                }
            },
        }
    );
    // 회원권 업그레이드
    const patchMembershipUpgradeMutation = useMutation(
        ['patchMembershipUpgradeMutation'],
        async (params: Omit<PatchMembershipRefundAndUpgradedParams, 'accessToken'>) =>
            patchMembershipUpgrade({ ...params, accessToken: authorization }),
        {
            onError: (error) => {
                console.log(error);
            },
            onSuccess: async ({ status, data }) => {
                if (status === 200 || status === 201) {
                    onPatchMembershipUpgradeSuccess && onPatchMembershipUpgradeSuccess({ status, data });
                    void invalidatePaymentRelatedQueries();
                }
            },
        }
    );
    // 분할결제 전환 / 미수금 결제
    const postSplitPaymentMutation = useMutation(
        ['postSplitPaymentMutation'],
        async (params: Omit<PostSplitPaymentParams, 'accessToken'>) =>
            postSplitPayment({ ...params, accessToken: authorization }),
        {
            onError: (error) => {
                console.log(error);
            },
            onSuccess: async (result: any) => {
                // PT: 201, 그룹: 200
                if (result?.status === 200 || result?.status === 201) {
                    onPostSplitPaymentSuccess && onPostSplitPaymentSuccess();
                    void invalidatePaymentRelatedQueries();
                }
            },
        }
    );
    // 회원권 결제 수정
    const patchMembershipPaymentMutation = useMutation(
        ['patchMembershipPaymentMutation'],
        async (params: Omit<PatchMembershipPaymentApiType, 'accessToken'>) =>
            patchMembershipPayment({ ...params, accessToken: authorization }),
        {
            onError: (error) => {
                console.log(error);
                onPatchMembershipPaymentFail && onPatchMembershipPaymentFail(null);
            },
            onSuccess: async (result) => {
                if (result?.status === 200) {
                    onPatchMembershipPaymentSuccess && onPatchMembershipPaymentSuccess();
                    void invalidatePaymentRelatedQueries();
                    return;
                }
                // 실패를 알리지 않으면 모달이 그대로 남아 관리자가 같은 버튼을 계속 누른다.
                onPatchMembershipPaymentFail && onPatchMembershipPaymentFail(result);
            },
        },
    );
    // 회원권 결제 삭제
    const deleteMembershipPaymentMutation = useMutation(
        ['deleteMembershipPaymentMutation'],
        async (params: {
            membershipPaymentId: string;
            source?: 'pt' | 'group';
            centerId?: string;
        }) => deleteMembershipPayment({ ...params, accessToken: authorization }),
        {
            onError: (error) => {
                console.log(error);
                onDeleteMembershipPaymentFail && onDeleteMembershipPaymentFail(null);
            },
            onSuccess: async (result) => {
                // PT: 204, 그룹: 200
                if (result?.status === 204 || result?.status === 200) {
                    onDeleteMembershipPaymentSuccess && onDeleteMembershipPaymentSuccess();
                    void invalidatePaymentRelatedQueries();
                    return;
                }
                onDeleteMembershipPaymentFail && onDeleteMembershipPaymentFail(result);
            },
        },
    );

    return {
        postSalesManagerMutation,
        patchMembershipRefundMutation,
        patchMembershipUpgradeMutation,
        postSplitPaymentMutation,
        patchMembershipPaymentMutation,
        deleteMembershipPaymentMutation,
    };
};

export default useMembershipPaymentsMutationService;
